import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Grid, IconButton, Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RouterIcon from '@mui/icons-material/Router';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import { PageHeader } from '../../components/ui/PageHeader';
import { DataTable } from '../../components/common/DataTable';
import { SearchableEntitySelect, EntityOption } from '../../components/common/SearchableEntitySelect';
import { gpsAPI, vehicleAPI, driverAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLocale } from '../../contexts/LocaleContext';
import { getPageSubtitle } from '../../constants/pageSubtitles';

const DEVICE_TYPES = [
  { value: 'ais140', label: 'AIS-140 Compliant' },
  { value: 'commercial', label: 'Commercial Vehicle' },
  { value: 'auto_rickshaw', label: 'Auto / Rickshaw' },
  { value: 'fleet', label: 'Fleet Tracker' },
  { value: 'other', label: 'Other' },
];

const PROVIDERS = [
  'generic', 'ais140', 'commercial', 'auto_rickshaw', 'fleet', 'loconav', 'fleetx', 'trackobit', 'other',
];

const emptyForm = () => ({
  deviceId: '', imeiNumber: '', deviceName: '', deviceType: 'commercial',
  provider: 'commercial', simNumber: '', vehicle: '', driver: '', supportsIgnition: false,
});

export const GPSDevicesPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const { formatDate } = useLocale();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [vehicleOptions, setVehicleOptions] = useState<EntityOption[]>([]);
  const [driverOptions, setDriverOptions] = useState<EntityOption[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [simulatingId, setSimulatingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await gpsAPI.getDevices();
      setRows(res.data || []);
    } catch {
      showToast('Failed to load GPS devices', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    load();
    vehicleAPI.getAll().then((r) => {
      setVehicleOptions((r.data || []).map((v: Record<string, unknown>) => ({
        _id: String(v._id),
        label: String(v.vehicleNumber),
      })));
    });
    driverAPI.getAll().then((r) => {
      setDriverOptions((r.data || []).map((d: Record<string, unknown>) => ({
        _id: String(d._id),
        label: String(d.driverName),
      })));
    });
  }, [load]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (row: Record<string, unknown>) => {
    setEditId(String(row._id));
    setForm({
      deviceId: String(row.deviceId || ''),
      imeiNumber: String(row.imeiNumber || ''),
      deviceName: String(row.deviceName || ''),
      deviceType: String(row.deviceType || 'commercial'),
      provider: String(row.provider || 'commercial'),
      simNumber: String(row.simNumber || ''),
      vehicle: String((row.vehicle as Record<string, unknown>)?._id || row.vehicle || ''),
      driver: String((row.driver as Record<string, unknown>)?._id || row.driver || ''),
      supportsIgnition: Boolean(row.supportsIgnition),
    });
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.vehicle) {
      showToast('Link a vehicle before saving the GPS device', 'error');
      return;
    }
    try {
      setSaving(true);
      const payload = { ...form, vehicle: form.vehicle || null, driver: form.driver || null };
      if (editId) {
        await gpsAPI.updateDevice(editId, payload);
        showToast('GPS device updated', 'success');
      } else {
        await gpsAPI.createDevice(payload);
        showToast('GPS device created', 'success');
      }
      setOpen(false);
      load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Save failed';
      showToast(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Remove this GPS device?')) return;
    try {
      await gpsAPI.deleteDevice(id);
      showToast('Device removed', 'success');
      load();
    } catch {
      showToast('Delete failed', 'error');
    }
  };

  const handleSimulateLocation = async (row: Record<string, unknown>) => {
    const id = String(row._id);
    const imei = String(row.imeiNumber || '');
    const vehicle = row.vehicle as Record<string, unknown> | undefined;
    if (!vehicle?._id && !vehicle) {
      showToast('Link this device to a vehicle first', 'error');
      return;
    }
    try {
      setSimulatingId(id);
      await gpsAPI.simulateLocation(id, {
        imei,
        latitude: 28.6139,
        longitude: 77.209,
        speed: 35,
        address: 'New Delhi (test location)',
      });
      showToast('Test location sent — open Live Map (GPS) to see it', 'success');
      load();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Failed to send test location';
      showToast(msg, 'error');
    } finally {
      setSimulatingId(null);
    }
  };

  const columns = [
    { field: 'deviceId', header: 'Device ID' },
    { field: 'imeiNumber', header: 'IMEI' },
    { field: 'simNumber', header: 'SIM' },
    { field: 'provider', header: 'Provider' },
    { field: 'deviceType', header: 'Type' },
    {
      field: 'vehicle',
      header: 'Vehicle',
      render: (row: Record<string, unknown>) => {
        const v = row.vehicle as Record<string, unknown> | undefined;
        return v?.vehicleNumber ? String(v.vehicleNumber) : '—';
      },
    },
    {
      field: 'vehicle',
      header: 'Customer',
      render: (row: Record<string, unknown>) => {
        const v = row.vehicle as Record<string, unknown> | undefined;
        const c = v?.client as Record<string, unknown> | undefined;
        return c?.clientName ? String(c.clientName) : '—';
      },
    },
    { field: 'status', header: 'Status' },
    {
      field: 'lastSeenAt',
      header: 'Last Seen',
      render: (row: Record<string, unknown>) => (row.lastSeenAt ? formatDate(String(row.lastSeenAt), { dateStyle: 'medium', timeStyle: 'short' }) : '—'),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="GPS Devices"
        subtitle={getPageSubtitle('gps_devices')}
        icon={<RouterIcon />}
        action={
          hasPermission('gps_tracking', 'create') ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              Add Device
            </Button>
          ) : undefined
        }
      />

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        search={search}
        onSearchChange={setSearch}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        getRowId={(row) => String(row._id)}
        onEdit={hasPermission('gps_tracking', 'edit') ? openEdit : undefined}
        onDelete={hasPermission('gps_tracking', 'delete') ? (row) => handleDelete(String(row._id)) : undefined}
        canEdit={hasPermission('gps_tracking', 'edit')}
        canDelete={hasPermission('gps_tracking', 'delete')}
        emptyMessage="No GPS devices registered. Add a device and link it to a vehicle."
        renderActions={(row) => (
          hasPermission('gps_tracking', 'edit') ? (
            <Tooltip title="Send test location to show vehicle on map">
              <span>
                <IconButton
                  size="small"
                  disabled={simulatingId === String(row._id) || !(row.vehicle as Record<string, unknown>)?._id}
                  onClick={() => handleSimulateLocation(row)}
                >
                  <MyLocationIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          ) : null
        )}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Edit GPS Device' : 'Add GPS Device'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Device ID" required value={form.deviceId} onChange={(e) => setForm({ ...form, deviceId: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="IMEI Number" required value={form.imeiNumber} onChange={(e) => setForm({ ...form, imeiNumber: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Device Name" required value={form.deviceName} onChange={(e) => setForm({ ...form, deviceName: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Device Type" value={form.deviceType} onChange={(e) => setForm({ ...form, deviceType: e.target.value })}>
                {DEVICE_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select fullWidth label="Provider" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })}>
                {PROVIDERS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="SIM Number" value={form.simNumber} onChange={(e) => setForm({ ...form, simNumber: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <SearchableEntitySelect
                label="Link Vehicle"
                value={form.vehicle}
                options={vehicleOptions}
                onChange={(v) => setForm({ ...form, vehicle: v })}
                placeholder="Select vehicle..."
                required
              />
            </Grid>
            <Grid item xs={12}>
              <SearchableEntitySelect
                label="Driver (optional)"
                value={form.driver}
                options={driverOptions}
                onChange={(v) => setForm({ ...form, driver: v })}
                placeholder="Select driver..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !form.deviceId || !form.imeiNumber || !form.deviceName || !form.vehicle}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
